// Display-only per-page pricing for Storiz orders.
//
// Storiz pricing is PER-PAGE: a bundle (Solo/Duo/Trio) includes a base page
// allowance at a base price; pages beyond that allowance cost extra at the
// admin-configurable per-page rate (`app_settings.price_per_page`).
//
// ⚠️ The allowance is PER ORDER, not global. Migration 082 froze it onto
// `orders.base_pages_per_album` at creation, so an order sold under the old
// 50-page base keeps quoting 50 forever while new orders are sold at 36. Read
// the order's snapshot (`resolveBasePagesPerAlbum`) wherever an order row is in
// hand; `BASE_PAGES_PER_ALBUM` is only the fallback for when it is missing.
//
// This module is the single front-end source of truth for turning a stored
// order row into an up-to-date price breakdown. It mirrors EXACTLY the
// authoritative computation the worker performs in
// `worker/src/routes/orders.ts` (the `/checkout-summary` self-heal):
//
//   base          = total_price - persisted extra_pages_cost - relief_3d_cost
//   liveExtraCost = liveExtraPages * pricePerPage
//   liveTotal     = base + liveExtraCost + relief_3d_cost
//
// The subtraction of the persisted extras is what prevents double-counting:
// `total_price` may already include extras the worker wrote on a previous
// submit, so we unwind them before re-applying the live extras. The Relief 3D
// add-on (migration 069) is unwound and re-added the same way.
//
// `relief_3d_cost` is REQUIRED rather than optional on purpose: a defaulted
// field would let a screen quietly show a total LOWER than what the customer is
// actually charged. Making it required turns that into a compile error.
//
// NOTHING here writes — it is purely for rendering the current price.

/** Pages included in a bundle's base price, per album — 18 papers = 36 pages.
 *  This is the allowance stamped onto a NEW order; it is NOT the allowance of
 *  an order that already exists. Every order carries its own frozen
 *  `orders.base_pages_per_album` (migration 082), and orders sold before
 *  2026-08-12 carry 50. Prefer `resolveBasePagesPerAlbum(order)` wherever an
 *  order row is in hand — this constant is only the fallback.
 *
 *  Matches `BASE_PAGES_PER_ALBUM` in worker/src/lib/orderPricing.ts and the
 *  `/checkout-summary` handler in worker/src/routes/orders.ts. */
export const BASE_PAGES_PER_ALBUM = 36;

/**
 * Resolve the page allowance to bill an order against.
 *
 * Mirrors the worker's coercion in `worker/src/lib/orderPricing.ts` EXACTLY: a
 * missing, non-finite or non-positive value falls back to
 * `BASE_PAGES_PER_ALBUM` — never to 0. That direction is deliberate. A fallback
 * of 0 would make every page of every album read as a billable extra, so a
 * screen that failed to pass the snapshot would quote a wildly inflated total.
 *
 * ⚠️ Display-only, like the rest of this module. The worker is authoritative;
 * this only has to agree with it.
 */
export function resolveBasePagesPerAlbum(basePagesPerAlbum?: number | null): number {
  const raw = Number(basePagesPerAlbum);
  return Number.isFinite(raw) && raw > 0 ? Math.round(raw) : BASE_PAGES_PER_ALBUM;
}

/** What every order before 2026-10-10 was sold at. Mirrors LEGACY_PRICE_PER_PAGE in worker/src/lib/pagePrice.ts. */
export const LEGACY_PRICE_PER_PAGE = 8;

/**
 * The extra-page price ON THIS ORDER — `orders.price_per_page`, frozen at
 * creation (migration 230, owner 2026-10-10: « every price reads the order's
 * saved price — never a global number »).
 *
 * ⚠️ The fallback is 8, never `app_settings.price_per_page`: that setting only
 * reaches NEW orders. Mirrors `orderPagePrice` in worker/src/lib/pagePrice.ts.
 */
export function resolvePricePerPage(order?: { price_per_page?: number | string | null } | null): number {
  const v = Number(order?.price_per_page);
  return Number.isFinite(v) && v > 0 ? v : LEGACY_PRICE_PER_PAGE;
}

export interface OrderPriceInput {
  /** Stored bundle base + any extras the worker persisted on a prior submit. */
  total_price: number | null | undefined;
  /** Extra-page surcharge the worker last persisted (subtracted to recover the
   *  pure bundle base). */
  extra_pages_cost?: number | null;
  /** Relief 3D add-on charge persisted on the order (`orders.relief_3d_cost`).
   *  Pass 0 when the order has no add-on. Required — see the module header. */
  relief_3d_cost: number | null | undefined;
  /** Accepted add-ons OTHER than Relief (`orders.addons_cost`, migration 188 —
   *  express delivery accepted in the chat), already inside total_price.
   *  Unwound from the base and re-added, like the Relief, so the admin can show
   *  it as its own line. Optional: omitting it leaves it inside the base and
   *  the total unchanged — the worker's pass-through, mirrored. */
  addons_cost?: number | null;
  /** LIVE extra pages to bill. Pass the up-to-date count derived from the
   *  order's current page_count (see `liveExtraPagesForSingleAlbum` /
   *  caller-summed multi-album value). */
  extraPages: number;
  /** Admin-configured per-page rate (app_settings.price_per_page). */
  pricePerPage: number;
}

export interface OrderPriceBreakdown {
  /** Pure bundle base, with any previously-persisted extras unwound. Never
   *  negative. */
  basePrice: number;
  /** Live extra pages billed. */
  extraPages: number;
  /** Live extra-page surcharge = extraPages * pricePerPage. */
  extraCost: number;
  /** Relief 3D add-on charge, passed straight through (0 when unsold). */
  reliefCost: number;
  /** Other accepted add-ons, passed straight through (0 when none). */
  addonsCost: number;
  /** Up-to-date grand total = basePrice + extraCost + reliefCost + addonsCost. */
  total: number;
}

/**
 * Compute the up-to-date price breakdown for an order, reflecting its CURRENT
 * page count rather than whatever total was last persisted.
 *
 * Display-only. Mirrors the worker's authoritative formula so the admin and
 * customer surfaces never drift from what the customer will actually be
 * charged at checkout.
 */
export function computeOrderPrice(input: OrderPriceInput): OrderPriceBreakdown {
  const totalPrice = Number(input.total_price) || 0;
  const persistedExtraCost = Number(input.extra_pages_cost) || 0;
  const pricePerPage = Number(input.pricePerPage) || 0;
  const extraPages = Math.max(0, Math.round(Number(input.extraPages) || 0));

  // Unwind any persisted extras to recover the pure bundle base, then clamp
  // so a corrupt row (extras > total) can never show a negative base.
  const reliefCost = Number(input.relief_3d_cost) || 0;
  const addonsCost = Number(input.addons_cost) || 0;
  const basePrice = Math.max(0, totalPrice - persistedExtraCost - reliefCost - addonsCost);
  const extraCost = extraPages * pricePerPage;
  const total = basePrice + extraCost + reliefCost + addonsCost;

  return { basePrice, extraPages, extraCost, reliefCost, addonsCost, total };
}

/**
 * Live extra-page count for a single album given its current page count.
 * Pages beyond the per-album base allowance are billable extras.
 *
 * `basePagesPerAlbum` is `orders.base_pages_per_album` — the allowance FROZEN
 * on this order at creation (migration 082). Pass it whenever the order row is
 * available, so an order sold under the old 50-page base keeps quoting 50.
 * Omitting it, or passing junk, falls back to `BASE_PAGES_PER_ALBUM` — never to
 * 0. See `resolveBasePagesPerAlbum`.
 */
export function liveExtraPagesForSingleAlbum(
  pageCount: number | null | undefined,
  basePagesPerAlbum?: number | null,
): number {
  return Math.max(0, (Number(pageCount) || 0) - resolveBasePagesPerAlbum(basePagesPerAlbum));
}
